(() => {
  'use strict';

  if (typeof window === 'undefined') return;

  const DRAWER_SELECTOR = '[data-mic-review]';
  const CREATE_REQUEST_KEY = 'micJourneyDraftCreateRequestV1';
  const ACCESS_KEY = 'micJourneyAccessV1';
  const PERSISTED_SELECTION_KEY = 'micJourneyPersistedSelectionV1';
  const SERVER_ITEM_KEYS = Object.freeze(['experienceId', 'title', 'city', 'productHandle', 'tags']);
  const cityFallbacks = Object.freeze({
    shanghai: 'https://cdn.shopify.com/s/files/1/0770/5706/5007/files/6320cf48a84676b28876c92e701351.jpg?v=1786242409',
    hangzhou: 'https://cdn.shopify.com/s/files/1/0770/5706/5007/collections/mic-ai-digital-collection-optimized.jpg?v=1787331606',
    suzhou: 'https://cdn.shopify.com/s/files/1/0770/5706/5007/files/mic-suzhou-lingering-garden.jpg?v=1787626357',
    chengdu: 'https://cdn.shopify.com/s/files/1/0770/5706/5007/files/chengdu-panda-base.jpg?v=1786244463',
    chongqing: 'https://cdn.shopify.com/s/files/1/0770/5706/5007/collections/4.jpg?v=1784966367',
    beijing: 'https://cdn.shopify.com/s/files/1/0770/5706/5007/t/55/assets/mic-place-beijing-commons.jpg',
    shenzhen: 'https://cdn.shopify.com/s/files/1/0770/5706/5007/collections/mic-ai-digital-collection-optimized.jpg?v=1787331606'
  });

  let initializedRoot = null;
  let deactivateAdapter = null;
  let handleDraftOpenRequest = null;

  window.addEventListener('mic:open-journey-draft', (event) => {
    const detail = event.detail && typeof event.detail === 'object' ? event.detail : {};
    if (handleDraftOpenRequest) handleDraftOpenRequest(detail);
    else window.MIC_JOURNEY_PENDING_DRAFT_OPEN = detail;
  });

  function initialize() {
    const draft = window.MICJourneyDraft;
    const originalReview = window.MICJourneyReview;
    const root = document.querySelector(DRAWER_SELECTOR);
    if (!draft || !originalReview || !root) return false;
    if (initializedRoot === root && root.dataset.sharedDraftAdapter === 'true') return true;

    const dialog = root.querySelector('[data-review-dialog]');
    const loading = root.querySelector('[data-review-loading]');
    const content = root.querySelector('[data-review-content]');
    const errorPanel = root.querySelector('[data-review-error]');
    const actions = root.querySelector('[data-review-actions]');
    const scroller = root.querySelector('[data-review-scroll]');
    const cityList = root.querySelector('[data-review-cities]');
    const actionButton = root.querySelector('[data-review-primary]');
    const wishlistButton = root.querySelector('[data-review-wishlist]');
    const quoteForm = root.querySelector('[data-review-quote-form]');
    const enquiryPanel = root.querySelector('[data-review-enquiry]');
    const requestState = root.querySelector('[data-review-request-state]');
    const quotePanel = root.querySelector('[data-review-quote]');
    if (!dialog || !loading || !content || !errorPanel || !actions || !scroller || !cityList || !actionButton || !wishlistButton || !quoteForm || !enquiryPanel || !requestState || !quotePanel) return false;

    if (deactivateAdapter) deactivateAdapter();
    initializedRoot = root;
    root.dataset.sharedDraftAdapter = 'true';

    const originalOpen = originalReview.open.bind(originalReview);
    const originalClose = originalReview.close.bind(originalReview);
    let draftActive = false;
    let activeScope = 'committed';
    let activePanel = 'wishlist';
    let draftLastFocus = null;
    let requestSerial = 0;
    let requestController = null;
    let requestBusy = false;
    let lastDraftOpenRequest = null;

    function setText(selector, value) {
      const element = root.querySelector(selector);
      if (element) element.textContent = value == null ? '' : String(value);
    }

    function setActionStatus(value, kind = '') {
      const element = root.querySelector('[data-review-action-status]');
      if (!element) return;
      element.textContent = value == null ? '' : String(value);
      element.classList.toggle('is-error', kind === 'error');
      element.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    }

    function focusQuoteDetails() {
      requestAnimationFrame(() => {
        const field = quoteForm.querySelector('input:not([disabled]),select:not([disabled]),textarea:not([disabled])');
        field?.focus({preventScroll:true});
        field?.scrollIntoView({behavior:'smooth',block:'center'});
      });
    }

    function quoteFormIsValid() {
      if (quoteForm.reportValidity()) return true;
      setActionStatus('Please complete the highlighted quote details before sending your request.', 'error');
      requestAnimationFrame(() => quoteForm.querySelector(':invalid')?.focus({preventScroll:false}));
      return false;
    }

    function safeImageUrl(value) {
      try {
        const url = new URL(String(value || ''), window.location.origin);
        return url.protocol === 'https:' ? url.href : '';
      } catch (error) {
        return '';
      }
    }

    function fallbackImage(cityId) {
      return cityFallbacks[String(cityId || '').trim().toLowerCase()] || cityFallbacks.shanghai;
    }

    function selectionForScope(snapshot = draft.getSnapshot()) {
      return snapshot?.[activeScope] || { cities: [], experiences: [], cityDays: {} };
    }

    function displayDays(value) {
      const days = Number(value);
      if (!Number.isFinite(days)) return '1';
      return Number.isInteger(days) ? String(days) : days.toFixed(1).replace(/\.0$/, '');
    }

    function totalDays(selection) {
      return (selection.cities || []).reduce((sum, city) => {
        const days = Number(selection.cityDays?.[city.id]);
        return sum + (Number.isFinite(days) && days > 0 ? days : 1);
      }, 0);
    }

    function addEmailField() {
      let input = quoteForm.elements.namedItem('email');
      if (input) return input;

      const wrapper = document.createElement('div');
      wrapper.className = 'mic-journey-drawer__field mic-journey-drawer__field--wide mic-journey-drawer__draft-email';
      const label = document.createElement('label');
      label.htmlFor = 'mic-quote-email';
      label.textContent = 'Email address';
      input = document.createElement('input');
      input.id = 'mic-quote-email';
      input.name = 'email';
      input.type = 'email';
      input.autocomplete = 'email';
      input.required = true;
      wrapper.append(label, input);

      const phoneField = quoteForm.querySelector('[name="phone"]')?.closest('.mic-journey-drawer__field');
      quoteForm.insertBefore(wrapper, phoneField || quoteForm.firstChild);
      return input;
    }

    const emailInput = addEmailField();
    const emailField = emailInput.closest('.mic-journey-drawer__field');

    function prepareServerForm() {
      emailInput.required = false;
      emailInput.disabled = true;
      if (emailField) emailField.hidden = true;
    }

    prepareServerForm();

    function resetDraftForm() {
      quoteForm.reset();
      quoteForm.querySelectorAll('input,select,textarea').forEach((field) => { field.disabled = false; });
      if (emailField) emailField.hidden = false;
      emailInput.required = true;
      emailInput.disabled = false;
      enquiryPanel.hidden = false;
      requestState.hidden = true;
      quotePanel.hidden = true;
      setActionStatus('');
      setText('[data-review-quote-action-status]', '');
      requestBusy = false;
      actionButton.disabled = false;
    }

    function setDraftPanel(panel, options = {}) {
      activePanel = panel === 'quote' ? 'quote' : 'wishlist';
      root.querySelectorAll('[data-review-view]').forEach((view) => {
        view.hidden = view.dataset.reviewView !== activePanel;
      });
      root.querySelectorAll('[data-review-tab]').forEach((button) => {
        button.setAttribute('aria-selected', String(button.dataset.reviewTab === activePanel));
      });
      wishlistButton.setAttribute('aria-pressed', String(activePanel === 'wishlist'));
      wishlistButton.textContent = activePanel === 'wishlist' ? 'Continue Editing' : 'View My Journey';
      actionButton.textContent = activePanel === 'wishlist' ? 'Get a Quote' : requestBusy ? 'Sending…' : 'Submit Quote Request';
      actionButton.disabled = requestBusy;
      if (!options.keepScroll) {
        try { scroller.scrollTo({ top: 0, behavior: options.instant ? 'auto' : 'smooth' }); } catch (error) { scroller.scrollTop = 0; }
      }
    }

    function makeProductTitle(experience) {
      const title = experience.title || experience.experienceId || 'Journey experience';
      if (!experience.handle) {
        const strong = document.createElement('strong');
        strong.textContent = title;
        return strong;
      }
      const link = document.createElement('a');
      link.className = 'mic-journey-drawer__draft-product-link';
      link.href = `/products/${encodeURIComponent(experience.handle)}`;
      link.textContent = title;
      return link;
    }

    function renderDraftCities(selection) {
      cityList.innerHTML = '';
      const experiences = Array.isArray(selection.experiences) ? selection.experiences : [];

      (selection.cities || []).forEach((city, index) => {
        const cityExperiences = experiences.filter((experience) => experience.cityId === city.id);
        const firstImage = cityExperiences.find((experience) => safeImageUrl(experience.imageUrl));
        const imageUrl = safeImageUrl(firstImage?.imageUrl) || fallbackImage(city.id);
        const days = Number(selection.cityDays?.[city.id]) || 1;

        const section = document.createElement('section');
        section.className = 'mic-journey-drawer__city mic-journey-drawer__draft-city';

        const toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'mic-journey-drawer__city-toggle';
        toggle.setAttribute('aria-expanded', 'true');
        const number = document.createElement('span');
        number.className = 'mic-journey-drawer__city-number';
        number.textContent = String(index + 1).padStart(2, '0');
        const title = document.createElement('strong');
        title.textContent = city.name;
        const dayBadge = document.createElement('span');
        dayBadge.className = 'mic-journey-drawer__city-days';
        dayBadge.textContent = `${displayDays(days)} ${days === 1 ? 'day' : 'days'}`;
        const chevron = document.createElement('span');
        chevron.className = 'mic-journey-drawer__chevron';
        toggle.append(number, title, dayBadge, chevron);

        const body = document.createElement('div');
        body.className = 'mic-journey-drawer__city-body';
        const media = document.createElement('div');
        media.className = 'mic-journey-drawer__city-media';
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = firstImage?.imageAlt || `${city.name} journey`;
        image.loading = index === 0 ? 'eager' : 'lazy';
        const mediaLabel = document.createElement('p');
        mediaLabel.textContent = `${cityExperiences.length} selected ${cityExperiences.length === 1 ? 'experience' : 'experiences'}`;
        media.append(image, mediaLabel);

        const dayControls = document.createElement('div');
        dayControls.className = 'mic-journey-drawer__draft-city-controls';
        const dayStepper = document.createElement('div');
        dayStepper.className = 'mic-journey-drawer__draft-day-stepper';
        const minus = document.createElement('button');
        minus.type = 'button';
        minus.dataset.draftAction = 'decrease-days';
        minus.dataset.cityId = city.id;
        minus.setAttribute('aria-label', `Remove one day from ${city.name}`);
        minus.textContent = '−';
        const dayOutput = document.createElement('output');
        dayOutput.textContent = `${displayDays(days)} ${days === 1 ? 'day' : 'days'}`;
        const plus = document.createElement('button');
        plus.type = 'button';
        plus.dataset.draftAction = 'increase-days';
        plus.dataset.cityId = city.id;
        plus.setAttribute('aria-label', `Add one day to ${city.name}`);
        plus.textContent = '+';
        dayStepper.append(minus, dayOutput, plus);
        const removeCity = document.createElement('button');
        removeCity.type = 'button';
        removeCity.className = 'mic-journey-drawer__draft-remove-city';
        removeCity.dataset.draftAction = 'remove-city';
        removeCity.dataset.cityId = city.id;
        removeCity.textContent = 'Remove city';
        dayControls.append(dayStepper, removeCity);

        const label = document.createElement('p');
        label.className = 'mic-journey-drawer__experience-label';
        label.textContent = 'Included experiences';
        const list = document.createElement('ol');
        list.className = 'mic-journey-drawer__experiences';
        if (!cityExperiences.length) {
          const empty = document.createElement('li');
          empty.className = 'mic-journey-drawer__experience mic-journey-drawer__draft-empty';
          const emptyTitle = document.createElement('strong');
          emptyTitle.textContent = 'No experience selected for this city yet.';
          empty.append(emptyTitle);
          list.append(empty);
        }

        cityExperiences.forEach((experience) => {
          const item = document.createElement('li');
          item.className = 'mic-journey-drawer__experience mic-journey-drawer__draft-experience';
          const copy = document.createElement('div');
          const itemTitle = makeProductTitle(experience);
          const meta = document.createElement('p');
          meta.textContent = (experience.tags || []).slice(0, 3).join(' · ');
          copy.append(itemTitle, meta);
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'mic-journey-drawer__draft-remove-experience';
          remove.dataset.draftAction = 'remove-experience';
          remove.dataset.experienceKey = experience.experienceId || experience.handle || experience.id;
          remove.setAttribute('aria-label', `Remove ${experience.title || 'experience'}`);
          remove.textContent = 'Remove';
          item.append(copy, remove);
          list.append(item);
        });

        body.append(media, dayControls, label, list);
        toggle.addEventListener('click', () => {
          const collapsed = !body.hidden;
          body.hidden = collapsed;
          section.classList.toggle('is-collapsed', collapsed);
          toggle.setAttribute('aria-expanded', String(!collapsed));
        });
        section.append(toggle, body);
        cityList.append(section);
      });

      if (!(selection.cities || []).length) {
        const empty = document.createElement('section');
        empty.className = 'mic-journey-drawer__draft-empty-journey';
        const title = document.createElement('h2');
        title.textContent = 'Your journey is ready to begin.';
        const copy = document.createElement('p');
        copy.textContent = 'Choose a destination or experience, then reopen this panel to review it here.';
        empty.append(title, copy);
        cityList.append(empty);
      }
    }

    function renderDraft(snapshot = draft.getSnapshot()) {
      if (!draftActive) return;
      const selection = selectionForScope(snapshot);
      const cities = Array.isArray(selection.cities) ? selection.cities : [];
      const experiences = Array.isArray(selection.experiences) ? selection.experiences : [];
      const reference = snapshot.reference || {};
      const isPreview = activeScope === 'preview';

      setText('[data-review-id]', isPreview ? 'PREVIEW' : reference.journeyId || 'DRAFT');
      setText('[data-review-route]', cities.length > 1 ? `${cities[0].name} to ${cities[cities.length - 1].name}` : cities[0]?.name || 'Your China journey');
      setText('[data-review-days]', displayDays(totalDays(selection)));
      setText('[data-review-city-count]', cities.length);
      setText('[data-review-experience-total]', experiences.length);
      setText('[data-review-route-label]', isPreview ? 'STARTING MIX PREVIEW' : 'EDITABLE JOURNEY DRAFT');
      setText('[data-review-state]', isPreview ? 'Not saved' : reference.journeyId ? 'Saved draft' : 'Draft');
      setText('[data-review-state-copy]', isPreview
        ? 'This preview is not saved. Add it to your journey or submit a quote request when ready.'
        : 'Changes here update the same journey draft across the homepage and Builder. No order or payment has been created.');
      renderDraftCities(selection);

      loading.hidden = true;
      errorPanel.hidden = true;
      content.hidden = false;
      actions.hidden = false;
      enquiryPanel.hidden = false;
      requestState.hidden = true;
      quotePanel.hidden = true;
      quoteForm.querySelectorAll('input,select,textarea').forEach((field) => { field.disabled = false; });
      setDraftPanel(activePanel, { keepScroll: true });
    }

    function deactivateDraft(options = {}) {
      if (!draftActive) return;
      draftActive = false;
      lastDraftOpenRequest = null;
      requestSerial += 1;
      requestController?.abort();
      requestController = null;
      requestBusy = false;
      root.classList.remove('is-shared-draft');
      delete root.dataset.draftScope;
      quoteForm.reset();
      prepareServerForm();
      if (options.restoreFocus && draftLastFocus?.isConnected) {
        requestAnimationFrame(() => draftLastFocus?.focus({ preventScroll: true }));
      }
    }

    function openDraft(options = {}) {
      const requestedScope = options.scope === 'preview' ? 'preview' : 'committed';
      const requestedPanel = options.panel === 'quote' ? 'quote' : 'wishlist';
      const repeatedRequest = draftActive && (
        options === lastDraftOpenRequest
        || (options.requestId && options.requestId === lastDraftOpenRequest?.requestId)
      );
      if (repeatedRequest && activeScope === requestedScope && activePanel === requestedPanel) {
        renderDraft();
        return Promise.resolve(draft.getSnapshot());
      }
      lastDraftOpenRequest = options;
      activeScope = requestedScope;
      activePanel = requestedPanel;
      draftLastFocus = options.trigger instanceof HTMLElement
        ? options.trigger
        : document.activeElement instanceof HTMLElement ? document.activeElement : null;
      requestSerial += 1;
      requestController?.abort();
      requestController = null;
      draftActive = true;
      root.classList.add('is-shared-draft');
      root.dataset.draftScope = activeScope;
      root.hidden = false;
      document.body.classList.add('mic-review-open');
      resetDraftForm();
      renderDraft();
      setDraftPanel(activePanel, { instant: true });
      requestAnimationFrame(() => dialog.focus({ preventScroll: true }));
      return Promise.resolve(draft.getSnapshot());
    }

    function closeDraftOrServer() {
      const wasDraft = draftActive;
      deactivateDraft({ restoreFocus: wasDraft });
      return originalClose();
    }

    function continueEditingJourney() {
      closeDraftOrServer();
      const builderPath = '/pages/plan-your-visit';
      if (window.location.pathname === builderPath) {
        const builder = document.querySelector('[data-mic-journey-builder], [data-journey-builder], #mic-journey-builder');
        if (builder) {
          builder.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
      window.location.assign(builderPath);
    }

    function readStoredAccess() {
      try {
        const stored = JSON.parse(localStorage.getItem(ACCESS_KEY) || 'null');
        return stored && typeof stored === 'object' ? stored : {};
      } catch (error) {
        return {};
      }
    }

    function storeAccess(identity) {
      const safeIdentity = {
        journeyId: String(identity?.journeyId || ''),
        accessToken: String(identity?.accessToken || ''),
        customerId: String(identity?.customerId || '')
      };
      if (!safeIdentity.journeyId || !safeIdentity.accessToken) return;
      draft.setReference({ journeyId: safeIdentity.journeyId, accessToken: safeIdentity.accessToken });
      try {
        const current = readStoredAccess();
        localStorage.setItem(ACCESS_KEY, JSON.stringify({ ...current, ...safeIdentity }));
      } catch (error) {
        // The secure journey can still be used for this page session.
      }
    }

    function currentIdentity() {
      const reference = draft.getReference();
      if (reference.journeyId && reference.accessToken) return reference;
      const stored = readStoredAccess();
      if (stored.journeyId && stored.accessToken) return {
        journeyId: String(stored.journeyId),
        accessToken: String(stored.accessToken)
      };
      return { journeyId: '', accessToken: '' };
    }

    function uniqueInterests(selection) {
      const seen = new Set();
      const output = [];
      const append = (rawValue) => {
        const value = String(rawValue || '').trim();
        const key = value.toLocaleLowerCase();
        if (!value || seen.has(key) || output.length >= 20) return;
        seen.add(key);
        output.push(value.slice(0, 100));
      };
      (selection.interests || []).forEach(append);
      (selection.experiences || []).forEach((experience) => {
        (experience.tags || []).forEach((tag) => {
          append(tag);
        });
      });
      return output;
    }

    function buildServerSelection(selection, formValues) {
      const cities = (selection.cities || []).map((city) => city.name).filter(Boolean).slice(0, 10);
      const days = {};
      (selection.cities || []).slice(0, 10).forEach((city) => {
        const value = Number(selection.cityDays?.[city.id]);
        days[city.name] = Number.isFinite(value) ? Math.min(30, Math.max(1, Math.round(value))) : 1;
      });
      const experiences = Array.isArray(selection.experiences) ? selection.experiences : [];
      const items = experiences.slice(0, 50).map((experience) => {
        const source = {
          experienceId: experience.experienceId || experience.id,
          title: experience.title,
          city: experience.city,
          productHandle: experience.handle,
          tags: (experience.tags || []).slice(0, 12)
        };
        return Object.fromEntries(SERVER_ITEM_KEYS
          .filter((key) => source[key] != null && source[key] !== '' && (!Array.isArray(source[key]) || source[key].length))
          .map((key) => [key, source[key]]));
      });

      const marker = [
        '[MIC QUOTE REQUEST]',
        `Name: ${formValues.fullName}`,
        `Email: ${formValues.email}`,
        `Phone / WhatsApp: ${formValues.phone}`,
        `Preferred month: ${formValues.month}`,
        `Budget per person: ${formValues.budget}`,
        formValues.notes ? `Traveller notes: ${formValues.notes}` : '',
        '[/MIC QUOTE REQUEST]'
      ].filter(Boolean).join('\n');

      return {
        cities,
        days,
        interests: uniqueInterests(selection),
        items,
        groupType: '',
        groupSize: formValues.groupSize,
        travelDate: /^\d{4}-\d{2}$/.test(formValues.month) ? `${formValues.month}-01` : '',
        notes: marker.slice(0, 4000)
      };
    }

    function stableStringify(value) {
      if (value === null || typeof value !== 'object') return JSON.stringify(value);
      if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
      return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
    }

    function requestFingerprint(payload) {
      let hash = 2166136261;
      const input = stableStringify(payload);
      for (let index = 0; index < input.length; index += 1) {
        hash ^= input.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return (hash >>> 0).toString(16).padStart(8, '0');
    }

    function randomRequestId() {
      if (typeof window.crypto?.randomUUID === 'function') return `mic-web:${window.crypto.randomUUID()}`;
      const bytes = new Uint8Array(16);
      window.crypto?.getRandomValues?.(bytes);
      const fallback = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('') || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      return `mic-web:${fallback}`;
    }

    function createRequestId(payload) {
      const fingerprint = requestFingerprint(payload);
      try {
        const stored = JSON.parse(localStorage.getItem(CREATE_REQUEST_KEY) || 'null');
        if (stored?.clientRequestId && stored?.fingerprint === fingerprint) return stored.clientRequestId;
        const clientRequestId = randomRequestId();
        localStorage.setItem(CREATE_REQUEST_KEY, JSON.stringify({ clientRequestId, fingerprint }));
        return clientRequestId;
      } catch (error) {
        return randomRequestId();
      }
    }

    function clearCreateRequestId() {
      try { localStorage.removeItem(CREATE_REQUEST_KEY); } catch (error) {}
    }

    function markSelectionPersisted(journeyId, serverSelection) {
      try {
        localStorage.setItem(PERSISTED_SELECTION_KEY, JSON.stringify({
          journeyId,
          fingerprint: requestFingerprint(serverSelection)
        }));
      } catch (error) {}
    }

    function selectionWasPersisted(journeyId, serverSelection) {
      try {
        const stored = JSON.parse(localStorage.getItem(PERSISTED_SELECTION_KEY) || 'null');
        return stored?.journeyId === journeyId
          && stored?.fingerprint === requestFingerprint(serverSelection);
      } catch (error) {
        return false;
      }
    }

    async function jsonRequest(url, options = {}) {
      const response = await fetch(url, {
        ...options,
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json', ...(options.headers || {}) }
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.ok) {
        const error = new Error(payload?.error || 'The journey service is temporarily unavailable.');
        error.code = payload?.code || `http_${response.status}`;
        error.status = response.status;
        throw error;
      }
      return payload;
    }

    function splitName(fullName) {
      const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
      return { firstName: parts.shift() || 'MIC traveller', lastName: parts.join(' ') };
    }

    async function createJourney(serverSelection, formValues, signal, parentIdentity = null) {
      const names = splitName(formValues.fullName);
      const createPayload = {
        email: formValues.email,
        firstName: names.firstName,
        lastName: names.lastName,
        journey: serverSelection,
        ...(parentIdentity?.journeyId && parentIdentity?.accessToken ? {
          parentJourneyId: parentIdentity.journeyId,
          parentAccessToken: parentIdentity.accessToken
        } : {})
      };
      const clientRequestId = createRequestId(createPayload);
      const payload = await jsonRequest('/apps/mic-journeys/journeys', {
        method: 'POST',
        signal,
        body: JSON.stringify({ clientRequestId, ...createPayload })
      });
      const identity = {
        journeyId: payload.journeyId,
        accessToken: payload.accessToken,
        customerId: payload.customerId || ''
      };
      storeAccess(identity);
      markSelectionPersisted(identity.journeyId, serverSelection);
      clearCreateRequestId();
      return identity;
    }

    async function saveJourney(serverSelection, formValues, signal) {
      let identity = currentIdentity();
      if (!identity.journeyId || !identity.accessToken) return createJourney(serverSelection, formValues, signal);

      try {
        const payload = await jsonRequest(`/apps/mic-journeys/journeys/${encodeURIComponent(identity.journeyId)}`, {
          method: 'PATCH',
          signal,
          body: JSON.stringify({ accessToken: identity.accessToken, journey: serverSelection })
        });
        storeAccess({
          ...identity,
          customerId: payload.journey?.customerId || readStoredAccess().customerId || ''
        });
        markSelectionPersisted(identity.journeyId, serverSelection);
        return identity;
      } catch (error) {
        if (error.code === 'journey_locked' && selectionWasPersisted(identity.journeyId, serverSelection)) {
          return identity;
        }
        if (!['journey_locked', 'journey_not_found', 'journey_access_denied'].includes(error.code)) throw error;
        const parentIdentity = { ...identity };
        draft.setReference({ journeyId: '', accessToken: '' });
        try {
          return await createJourney(serverSelection, formValues, signal, parentIdentity);
        } catch (revisionError) {
          if (revisionError.code !== 'quote_not_revisable') throw revisionError;
          clearCreateRequestId();
          return createJourney(serverSelection, formValues, signal);
        }
      }
    }

    function quoteFormValues() {
      const formData = new FormData(quoteForm);
      return {
        fullName: String(formData.get('fullName') || '').trim().slice(0, 200),
        email: String(formData.get('email') || '').trim().toLowerCase().slice(0, 254),
        phone: String(formData.get('phone') || '').trim().slice(0, 200),
        month: String(formData.get('month') || '').trim(),
        groupSize: Number(formData.get('groupSize')),
        budget: String(formData.get('budget') || '').trim().slice(0, 200),
        notes: String(formData.get('notes') || '').trim().slice(0, 2000)
      };
    }

    function modulesStillInPreparation(selection) {
      const canonicalExperienceId = /^[A-Z]{2,3}-[A-Z]{2,4}-\d{3}$/;
      const validProductHandle = /^[a-z0-9][a-z0-9-]{0,100}$/;
      return (selection.experiences || []).filter((experience) => (
        !canonicalExperienceId.test(String(experience.experienceId || ''))
        || !validProductHandle.test(String(experience.handle || ''))
      ));
    }

    function preparationMessage(experiences) {
      const labels = experiences.slice(0, 3).map((experience) => (
        experience.title || experience.experienceId || 'Untitled module'
      ));
      const remainder = experiences.length - labels.length;
      const list = labels.join(', ') + (remainder > 0 ? ` and ${remainder} more` : '');
      return `Still in preparation: ${list}. Remove ${experiences.length === 1 ? 'this module' : 'these modules'} before requesting a quote.`;
    }

    async function submitDraftQuote() {
      if (!draftActive || requestBusy) return;

      const snapshotBeforeSubmit = draft.getSnapshot();
      let selection = selectionForScope(snapshotBeforeSubmit);
      const quoteCandidate = activeScope === 'preview'
        ? { experiences: [...(snapshotBeforeSubmit.committed.experiences || []), ...(selection.experiences || [])] }
        : selection;
      const unquotable = modulesStillInPreparation(quoteCandidate);
      if (unquotable.length) {
        setActionStatus(preparationMessage(unquotable), 'error');
        return;
      }

      if (!selection.cities.length) {
        setActionStatus('Choose at least one destination before requesting a quote.', 'error');
        return;
      }

      if (!quoteFormIsValid()) return;

      if (activeScope === 'preview') {
        activeScope = 'committed';
        root.dataset.draftScope = activeScope;
        draft.commitPreview({ strategy: 'merge' });
      }

      selection = selectionForScope();
      if (!selection.cities.length) {
        setActionStatus('Choose at least one destination before requesting a quote.', 'error');
        return;
      }

      const formValues = quoteFormValues();
      const serverSelection = buildServerSelection(selection, formValues);
      const serial = ++requestSerial;
      requestController?.abort();
      requestController = typeof AbortController === 'function' ? new AbortController() : null;
      requestBusy = true;
      setDraftPanel('quote', { keepScroll: true });
      setActionStatus('Saving this selection and creating its Journey ID…');

      try {
        const identity = await saveJourney(serverSelection, formValues, requestController?.signal);
        if (serial !== requestSerial) return;
        setActionStatus('Journey saved. Asking MIC to confirm availability…');
        await jsonRequest(`/apps/mic-journeys/journeys/${encodeURIComponent(identity.journeyId)}/request-confirmation`, {
          method: 'POST',
          signal: requestController?.signal,
          body: JSON.stringify({ accessToken: identity.accessToken })
        });
        if (serial !== requestSerial) return;

        requestBusy = false;
        requestController = null;
        deactivateDraft();
        await originalOpen({
          journeyId: identity.journeyId,
          accessToken: identity.accessToken,
          panel: 'quote'
        });
      } catch (error) {
        if (serial !== requestSerial || error?.name === 'AbortError') return;
        requestBusy = false;
        requestController = null;
        setDraftPanel('quote', { keepScroll: true });
        setActionStatus(error?.message || 'MIC could not receive the quote request. Please try again.', 'error');
      }
    }

    root.addEventListener('click', (event) => {
      if (!draftActive) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const closeButton = target.closest('[data-review-close]');
      if (closeButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeDraftOrServer();
        return;
      }

      const tab = target.closest('[data-review-tab]');
      if (tab) {
        event.preventDefault();
        event.stopImmediatePropagation();
        setDraftPanel(tab.dataset.reviewTab);
        return;
      }

      const wishlist = target.closest('[data-review-wishlist]');
      if (wishlist) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (activePanel === 'wishlist') continueEditingJourney();
        else setDraftPanel('wishlist');
        return;
      }

      const primary = target.closest('[data-review-primary]');
      if (primary) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (activePanel === 'wishlist') {
          setDraftPanel('quote');
          const snapshot = draft.getSnapshot();
          const selection = selectionForScope(snapshot);
          const quoteCandidate = activeScope === 'preview'
            ? { experiences: [...(snapshot.committed.experiences || []), ...(selection.experiences || [])] }
            : selection;
          const unquotable = modulesStillInPreparation(quoteCandidate);
          setActionStatus(
            unquotable.length
              ? preparationMessage(unquotable)
              : 'Complete the quote details below, then press “Submit Quote Request”.',
            unquotable.length ? 'error' : ''
          );
          focusQuoteDetails();
        }
        else void submitDraftQuote();
        return;
      }

      const control = target.closest('[data-draft-action]');
      if (!control) return;
      event.preventDefault();
      const action = control.dataset.draftAction;
      if (action === 'remove-city') {
        draft.remove(activeScope, 'city', control.dataset.cityId);
      } else if (action === 'remove-experience') {
        draft.remove(activeScope, 'experience', control.dataset.experienceKey);
      } else if (action === 'increase-days' || action === 'decrease-days') {
        const selection = selectionForScope();
        const current = Number(selection.cityDays?.[control.dataset.cityId]) || 1;
        const next = Math.min(30, Math.max(0.5, current + (action === 'increase-days' ? 1 : -1)));
        draft.setCityDays(activeScope, control.dataset.cityId, next);
      }
    }, true);

    quoteForm.addEventListener('input', (event) => {
      if (draftActive) event.stopImmediatePropagation();
    }, true);

    quoteForm.addEventListener('submit', (event) => {
      if (!draftActive) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      void submitDraftQuote();
    }, true);

    document.addEventListener('keydown', (event) => {
      if (!draftActive || event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      closeDraftOrServer();
    }, true);

    const unsubscribeDraft = draft.subscribe((snapshot) => {
      if (draftActive) renderDraft(snapshot);
    }, { immediate: false });

    const enhancedReview = {
      ...originalReview,
      open(options = {}) {
        deactivateDraft();
        prepareServerForm();
        return originalOpen(options);
      },
      close: closeDraftOrServer,
      openDraft,
      isDraftOpen: () => draftActive,
      getDraftScope: () => draftActive ? activeScope : ''
    };

    window.MICJourneyReview = enhancedReview;
    window.MICJourneyDrawer = enhancedReview;
    const currentHandleDraftOpenRequest = (detail = {}) => {
      if (detail.requestId) window.MIC_JOURNEY_LAST_DRAFT_OPEN_REQUEST_ID = detail.requestId;
      if (!detail.requestId || window.MIC_JOURNEY_PENDING_DRAFT_OPEN?.requestId === detail.requestId || window.MIC_JOURNEY_PENDING_DRAFT_OPEN === detail) {
        window.MIC_JOURNEY_PENDING_DRAFT_OPEN = null;
      }
      return openDraft(detail);
    };
    handleDraftOpenRequest = currentHandleDraftOpenRequest;
    deactivateAdapter = () => {
      draftActive = false;
      requestSerial += 1;
      requestController?.abort();
      requestController = null;
      unsubscribeDraft();
      if (handleDraftOpenRequest === currentHandleDraftOpenRequest) handleDraftOpenRequest = null;
    };

    const pendingDraftOpen = window.MIC_JOURNEY_PENDING_DRAFT_OPEN;
    if (pendingDraftOpen && typeof pendingDraftOpen === 'object') handleDraftOpenRequest(pendingDraftOpen);
    window.dispatchEvent(new CustomEvent('mic:journey-drawer-ready'));
    window.dispatchEvent(new CustomEvent('mic:journey-draft-ready'));
    return true;
  }

  window.addEventListener('mic:journey-review-ready', initialize);
  document.addEventListener('DOMContentLoaded', initialize, { once: true });
  initialize();
})();
